import React, { useRef, useEffect, useState } from 'react';
import {
  CarHistoryEvent,
  CarHistoryEventType,
  TimelineItemHSPI18n,
} from './CarHistoryTimeline.types';
import { BadgeStatusType, getStatusStyles } from '../Badge';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon';
import { APP_COLORS } from '@/config/colors';
import { Badge } from '../Badge/Badge';

interface TimelineItemProps {
  event: CarHistoryEvent;
  isFirst: boolean;
  isLast: boolean;
  i18n?: TimelineItemHSPI18n;
}

const getEventIcon = (
  type: CarHistoryEventType,
  color: string,
): React.ReactNode => {
  const iconClasses = 'w-5 h-5';

  switch (type) {
    case 'production':
      return <div className="w-4 h-4 rounded-full bg-primary" />;
    case 'first_registration':
      return <SVGIcon name="keys-on-hand" size="sm" fill={color} />;
    case 'owner_change':
      return <SVGIcon name="keys-on-hand" size="sm" fill={color} />;
    case 'technical_inspection':
      return <SVGIcon name="price-tag-sale" size="sm" fill={color} />;
    case 'accident':
      return <SVGIcon name="car-accident" size="sm" fill={color} />;
    case 'mileage_report':
      return <SVGIcon name="speedometer" size="sm" fill={color} />;
    case 'report_generation':
      return (
        <svg className={iconClasses} viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'no_data':
      return null;
    case 'other':
      return (
        <svg className={iconClasses} viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      return <div className="w-4 h-4 bg-gray-400 rounded-full" />;
  }
};

const mapEventTypeToStatusColor = (
  type: CarHistoryEventType, details?: CarHistoryEvent['details']
): { badge: BadgeStatusType; icon: string } => {
  switch (type) {
    case 'production':
      return {
        badge: BadgeStatusType.Info,
        icon: APP_COLORS.primary.DEFAULT,
      };
    case 'first_registration':
      return {
        badge: BadgeStatusType.Info,
        icon: APP_COLORS.primary.DEFAULT,
      };
    case 'owner_change':
      return {
        badge: BadgeStatusType.Info,
        icon: APP_COLORS.primary.DEFAULT,
      };
    case 'technical_inspection':
      return {
        badge: details?.inspectionResult === 'positive' ? BadgeStatusType.Success : BadgeStatusType.Warning2,
        icon: details?.inspectionResult === 'positive' ? APP_COLORS.green[700] : APP_COLORS.warning.iconColor,
      };
    case 'accident':
      return {
        badge: BadgeStatusType.Error,
        icon: APP_COLORS.red[800],
      };
    case 'report_generation':
      return {
        badge: BadgeStatusType.Success,
        icon: APP_COLORS.green[200],
      };
    case 'mileage_report':
      return {
        badge: BadgeStatusType.Neutral,
        icon: APP_COLORS.gray[400],
      };
    case 'no_data':
      return {
        badge: BadgeStatusType.Neutral,
        icon: APP_COLORS.gray[400],
      };
    case 'other':
      return {
        badge: BadgeStatusType.Neutral,
        icon: APP_COLORS.gray[400],
      };
    default:
      return {
        badge: BadgeStatusType.Info,
        icon: APP_COLORS.gray[400],
      };
  }
};

export const TimelineItemHSP: React.FC<TimelineItemProps> = ({
  event,
  isFirst,
  // isLast,
}) => {
  // const parsedDate = parse(event.date, 'dd.MM.yyyy', new Date());
  // const fullDate = format(parsedDate, 'dd.MM.yyyy');
  // const year = format(parsedDate, 'yyyy');
  // Dla "Brak danych" zawsze pokazujemy rok (bo nie ma markera roku)
  const showYear = isFirst || event.type === 'production';
  const isNoData = event.type === 'no_data';

  // i18n fallbacks
  // const ownerLabel = i18n?.ownerLabel || 'Właściciel:';
  // const ownerTypeIndividual = i18n?.ownerTypeIndividual || 'osoba fizyczna';
  // const ownerTypeCompany = i18n?.ownerTypeCompany || 'osoba prawna';
  // const voivodeshipLabel =
  //   i18n?.voivodeshipLabel || 'Województwo, w którym pojazd zarejestrowano:';
  // const previousOwnerDateTemplate =
  //   i18n?.previousOwnerDateTemplate || '(pozostał właścicielem do {date})';
  // const inspectionResultLabel = i18n?.inspectionResultLabel || 'Wynik badania:';
  // const inspectionResultPositive =
  //   i18n?.inspectionResultPositive || 'pozytywny';
  // const inspectionResultNegative =
  //   i18n?.inspectionResultNegative || 'negatywny';
  // const inspectionNumberLabel = i18n?.inspectionNumberLabel || 'Numer SKP:';

  // Dla "Brak danych" wyświetlamy rok lub zakres lat z pola yearRange
  const displayDate = isNoData
    ? event.yearRange || event.date.substring(0, 4)
    : event.date;

  // Refs do pomiaru odległości
  const dateRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [lineWidth, setLineWidth] = useState<number>(24);

  // Obliczamy rzeczywistą odległość między datą a opisem
  useEffect(() => {
    const calculateDistance = () => {
      if (!dateRef.current || !contentRef.current) return;

      const dateRect = dateRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();

      // Odległość to różnica między lewą krawędzią contentu a prawą krawędzią daty
      const distance = contentRect.left - dateRect.right;
      setLineWidth(Math.max(0, distance));
    };

    // Obliczamy od razu
    calculateDistance();

    // Obserwujemy zmiany rozmiaru
    const resizeObserver = new ResizeObserver(() => {
      calculateDistance();
    });

    if (dateRef.current) {
      resizeObserver.observe(dateRef.current);
    }
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Obserwujemy również zmiany okna
    window.addEventListener('resize', calculateDistance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateDistance);
    };
  }, [displayDate, event]);

  return (
    <div className="flex relative z-10 flex-col items-start mb-8 md:flex-row md:items-center">
      {/* Timeline left side with dates and icons */}
      <div className="flex relative flex-col mb-4 w-auto md:mb-0 md:w-48 md:items-center">
        {/* Year badge for first item */}
        {showYear ? (
          <div
            ref={dateRef}
            className="relative z-10 px-6 py-1 text-sm font-semibold tracking-wide uppercase bg-gray-100 rounded-full text-text-primary"
          >
            {displayDate}
            {/* Pozioma linia między datą a opisem wydarzenia */}
            <div
              className="absolute top-1/2 left-full z-0 h-px -translate-y-1/2"
              style={{
                width: `${lineWidth}px`,
                ...(isNoData
                  ? {
                    backgroundImage: `repeating-linear-gradient(
                        to right,
                        ${APP_COLORS.grayBorder} 0px,
                        ${APP_COLORS.grayBorder} 4px,
                        transparent 4px,
                        transparent 8px
                      )`,
                  }
                  : {
                    backgroundColor: APP_COLORS.grayBorder,
                  }),
              }}
            />
          </div>
        ) : (
          <div
            ref={dateRef}
            className={clsx(
              'relative z-10 rounded-full bg-gray-100 px-6 py-1 text-sm font-semibold uppercase tracking-wide text-text-primary md:whitespace-nowrap',
              isNoData && 'border border-dashed border-gray-300 bg-white',
            )}
          >
            {displayDate}
            {/* Pozioma linia między datą a opisem wydarzenia */}
            <div
              className="absolute top-1/2 left-full z-0 h-px -translate-y-1/2"
              style={{
                width: `${lineWidth}px`,
                ...(isNoData
                  ? {
                    backgroundImage: `repeating-linear-gradient(
                        to right,
                        ${APP_COLORS.grayBorder} 0px,
                        ${APP_COLORS.grayBorder} 4px,
                        transparent 4px,
                        transparent 8px
                      )`,
                  }
                  : {
                    backgroundColor: APP_COLORS.grayBorder,
                  }),
              }}
            />
          </div>
        )}

        {/* Connecting line */}
        {/* {!isLast && <div className="mt-3 w-px h-16 bg-primary-200" />} */}
      </div>

      {/* Event content */}
      <div
        ref={contentRef}
        className="relative flex-1 items-start w-full  md:items-center"
      >
        {/* <div className="absolute top-0 left-0 z-0 w-6 h-px bg-grayBorder"></div> */}

        <div
          className={clsx(
            'relative p-3 bg-white border shadow-sm rounded-card md:p-5 md:pl-10',
            isNoData ? 'px-5 border-gray-300 border-dashed' : 'pl-8',
          )}
        >
          {/* Icon container */}
          {!isNoData && (
            <div
              className={clsx(
                'absolute left-0 top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl',
                getStatusStyles(mapEventTypeToStatusColor(event.type, event.details).badge, 'v2'),
              )}
            >
              {getEventIcon(
                event.type,
                mapEventTypeToStatusColor(event.type, event.details).icon,
              )}
            </div>
          )}
          <div className="flex flex-col xl:w-1/2">
            {isNoData ? (
              <div>
                <p className="text-sm font-semibold text-grayText">
                  {event.title}
                </p>
                {event.description && (
                  <p className="mt-1 text-sm leading-relaxed text-grayText">
                    {event.yearRange
                      ? 'Brak informacji o zdarzeniach w tym okresie'
                      : typeof event.description === 'string'
                        ? event.description
                        : event.description}
                  </p>
                )}
              </div>
            ) : (
              <>
                <Badge
                  variant={mapEventTypeToStatusColor(event.type, event.details).badge}
                  badgeText={event.title}
                  version="v2"
                />
                {
                  event.description && (
                    <p className="mt-2 text-sm leading-relaxed text-text-primary">
                      {event.descriptionName && <span className="font-bold">{event.descriptionName} <br /></span>}
                      {event.description}
                    </p>
                  )
                  //   : (
                  //     <p
                  //       className="mt-2 text-sm leading-relaxed text-text-primary"
                  //       dangerouslySetInnerHTML={{ __html: event.description }}
                  //     />
                  //   )
                  // ) : (
                }
                {event.details && (
                  <div className="mt-3 space-y-1 text-sm leading-relaxed text-text-primary">
                    {/* {event.details.ownerType && (
                      <p>
                        {ownerLabel}{' '}
                        {event.details.ownerType === 'individual'
                          ? ownerTypeIndividual
                          : ownerTypeCompany}
                      </p>
                    )}
                    {event.details.voivodeship && (
                      <p>
                        {voivodeshipLabel} {event.details.voivodeship}
                      </p>
                    )}
                    {event.details.previousOwnerDate && (
                      <p>
                        {previousOwnerDateTemplate.replace(
                          '{date}',
                          event.details.previousOwnerDate,
                        )}
                      </p>
                    )} */}
                    {/* {event.details.inspectionResult && (
                      <p>
                        {inspectionResultLabel}{' '}
                        {event.details.inspectionResult === 'positive'
                          ? inspectionResultPositive
                          : inspectionResultNegative}
                      </p>
                    )}
                    {event.details.inspectionNumber && (
                      <p>
                        {inspectionNumberLabel} {event.details.inspectionNumber}
                      </p>
                    )} */}
                    {/* {event.details.mileage && (
                      <p>
                        {mileageLabel} {event.details.mileage.toLocaleString()}{' '}
                        {kmUnit}
                        {event.details.mileageNote &&
                          ` ${event.details.mileageNote}`}
                      </p>
                    )} */}
                    {/* {event.details.description && (
                      <p>{event.details.description}</p>
                    )} */}
                    {event.details.groups && (
                      <p>{event.details.groups.map((type) => type).join(', ')}</p>
                    )}
                    {event.details.value && (
                      <p>Wartość szkody: {event.details.value}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
